import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { CmsContextProvider } from './context';
import {
  EventHero, EventDescription, EventLineup, EventTickets, EventOpportunity,
} from './blocks/eventBlocks';
import { reportCms } from './diagnostics';
import { tierAvailability } from '../pages/public/eventAvailability';
import { useCopy } from '../copy/CopyProvider';
import type { EventDetail, EventOpportunity as Opportunity } from '../lib/catalog';

/**
 * The emergency Event composition.
 *
 * ── RESILIENCE WITHOUT A SECOND PRODUCT ──────────────────────────────────────────────
 *
 * Task 10 fell back to a complete second implementation of the Event page. That was the
 * right call while the CMS path was unproven and the wrong thing to keep: two full
 * compositions means every future change has to be made twice, and the one nobody
 * remembers to change is the one that runs when something has already gone wrong.
 *
 * This is the same blocks in the same order, composed in code instead of from a document.
 *
 * ── IT DOES NOT USE THE RENDERER IT IS COVERING FOR ──────────────────────────────────
 *
 * Blocks are invoked directly rather than through BlockRenderer. The renderer may be
 * exactly what just failed, and a fallback that depends on the mechanism it replaces is
 * not a fallback.
 *
 * ── EVERY SLOT IS INDIVIDUALLY GUARDED ───────────────────────────────────────────────
 *
 * The first version of this file was not, and the mutation check found the consequence:
 * when the ticket block threw, the CMS path fell back here, the same block threw again,
 * and the whole page died — the visitor could not even see which event it was. So each
 * slot has its own boundary, and a block that throws costs its own section and nothing
 * else.
 *
 * ── THE COMMERCE SURFACE IS THE ONE THING THAT MAY NOT VANISH ────────────────────────
 *
 * If the ticket panel itself cannot render, LastResortTickets takes its place: prices and
 * availability straight from the event payload, and a link to the checkout the panel
 * would have linked to. It shares nothing with EventTickets except the pure availability
 * rule, so a fault in that component cannot reach it. It is deliberately plain — it is
 * the thing you see when something is already wrong, and it exists so a visitor can still
 * buy a ticket.
 */

class SlotBoundary extends Component<
  { slot: string; critical?: boolean; fallback?: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() { return { failed: true }; }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    reportCms(
      this.props.critical ? 'cms_critical_block_failed' : 'cms_optional_block_failed',
      { component: this.props.slot, critical: Boolean(this.props.critical),
        reason: 'emergency_composition', message: error.message },
    );
  }

  render() {
    if (this.state.failed) return this.props.fallback ?? null;
    return this.props.children;
  }
}

/**
 * The minimum that still lets somebody buy a ticket.
 *
 * Reached only when EventTickets itself cannot render. Depends on nothing but the event
 * payload and the pure availability rule — which is also what decides whether the button
 * is live, so this cannot offer a purchase the real panel would have refused.
 */
function LastResortTickets({ event }: { event: EventDetail }) {
  const t = useCopy();
  const anyBuyable = event.tiers.some((tier) => tierAvailability(tier).buyable);

  // ── A NON-TICKETED EVENT MUST NOT BE TOLD IT IS "NOT ON SALE" ─────────────
  //
  // This panel is what renders when the admission surface itself has failed, and for a
  // request or RSVP event it would otherwise show an empty tier list and a disabled
  // "Not on sale" button — which is false: the event does admit people, through a form
  // this panel cannot draw.
  //
  // So it says what is true and sends them to the event, rather than inventing a form.
  // Read from the payload the page already has: no fetch, no extra dependency in the one
  // component whose whole purpose is to work when something else is broken.
  const mode = event.admission?.mode ?? 'ticketed';
  if (mode !== 'ticketed') {
    return (
      <div className="tickets glass-panel">
        <h2 className="tickets__title">{t('events.detail.tickets.heading', 'Tickets')}</h2>
        <p className="tickets__empty">
          {mode === 'invite_only'
            ? t('admissions.invite.required', 'This event is by invitation only.')
            : 'Admission for this event is handled through a short form, which could not be '
              + 'loaded just now. Please reload the page.'}
        </p>
        <button
          type="button"
          className="button button--primary button--block"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    );
  }

  return (
    <div className="tickets glass-panel">
      <h2 className="tickets__title">{t('events.detail.tickets.heading', 'Tickets')}</h2>

      {event.tiers.length === 0 ? (
        <p className="tickets__empty">
          {t('events.detail.tickets.empty', "Tickets for this event aren't on sale yet.")}
        </p>
      ) : (
        <ul className="tickets__list">
          {event.tiers.map((tier) => {
            const availability = tierAvailability(tier);
            return (
              <li
                key={tier.id}
                className={`tier glass-card${availability.buyable ? '' : ' tier--unavailable'}`}
              >
                <div className="tier__head">
                  <span className="tier__name">{tier.name}</span>
                  <span className="tier__price mono">
                    {(tier.priceCents / 100).toLocaleString(undefined, {
                      style: 'currency', currency: 'USD',
                    })}
                  </span>
                </div>
                <span className={`tier__availability tier__availability--${availability.tone}`}>
                  {availability.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {anyBuyable ? (
        <Link
          to={`/events/${event.id}/checkout`}
          className="button button--primary button--block tickets__cta"
        >
          {t('events.detail.cta.primary', 'Get tickets')}
        </Link>
      ) : (
        <button type="button" className="button button--primary button--block" disabled>
          {event.tiers.length === 0
            ? t('events.detail.cta.notOnSale', 'Not on sale')
            : t('events.detail.cta.soldOut', 'Sold out')}
        </button>
      )}
    </div>
  );
}

export function EmergencyEventPage({
  event, opportunity,
}: {
  event: EventDetail;
  opportunity: Opportunity | null;
}) {
  return (
    <CmsContextProvider value={{ kind: 'event', event, opportunity }}>
      <article className="detail">
        <SlotBoundary slot="EventHero">
          <EventHero />
        </SlotBoundary>

        <div className="detail__layout">
          <div className="detail__main">
            <SlotBoundary slot="EventDescription">
              <EventDescription heading="About" />
            </SlotBoundary>
            <SlotBoundary slot="EventLineup">
              <EventLineup heading="Lineup" showReliability />
            </SlotBoundary>
          </div>

          <aside className="detail__aside">
            {/* The only slot with a replacement rather than an omission. */}
            <SlotBoundary
              slot="EventTickets"
              critical
              fallback={<LastResortTickets event={event} />}
            >
              <EventTickets />
            </SlotBoundary>
          </aside>
        </div>

        <SlotBoundary slot="EventOpportunity">
          <EventOpportunity />
        </SlotBoundary>
      </article>
    </CmsContextProvider>
  );
}
